import { useState, useEffect } from 'react';
import {
  listVariantStats,
  createVariant,
  updateVariant,
  deleteVariant,
  promoteVariant,
} from '@/api/variants';
import type { PwaVariantStats } from '@/api/types';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { MediaUpload } from '@/components/MediaUpload';
import { Select } from '@/components/Select';
import { TrackerLinkPicker } from '@/components/TrackerLinkPicker';
import {
  getPwaApps,
  createPwaApp,
  updatePwaApp,
  deletePwaApp,
  uploadPwaMedia,
  translateDescriptions,
  translateReviews,
  getPwaStats,
} from '@/api/pwa';
import ReviewsEditor from '@/components/pwa/ReviewsEditor';
import { PwaGeneratorModal } from '@/components/pwa/PwaGeneratorModal';
import type { GeneratedPwaDraft } from '@/api/pwa';
import { getSettings, updateSettings } from '@/api/settings';
import { listAccounts, type FirebaseAccount } from '@/api/firebase';
import type {
  PwaApp,
  CreatePwaInput,
  UpdatePwaInput,
  PwaStatsResponse,
  PwaEngagementStats,
  PwaScrollDistribution,
  PwaReviewItem,
  PwaReviewI18nEntry,
} from '@/api/types';

type ModalMode = 'generate' | 'create' | 'edit' | 'delete' | 'stats' | null;

interface DescI18n {
  [lang: string]: { short: string; full: string };
}

interface PwaVideo {
  url: string;
  poster?: string;
}

interface FormData {
  app_name: string;
  developer_name: string;
  category: string;
  rating: number;
  reviews_count: number;
  downloads_count: string;
  size: string;
  version: string;
  short_description: string;
  full_description: string;
  icon_url: string;
  splash_url: string;
  bg_color: string;
  screenshots: string[];
  videos: PwaVideo[];
  offer_url: string;
  proxy_url: string;
  ab_enabled: boolean;
  ab_group: string;
  skip_landing_pct: number;
  status: 'active' | 'paused';
  store_layout: 'auto' | 'play' | 'app';
  enabled_languages: string[];
  descriptions_i18n: DescI18n;
  fb_pixel_id: string;
  fb_access_token: string;
  fb_test_event_code: string;
  fb_events: Record<string, string>;
  reviews: PwaReviewItem[];
  reviews_i18n: Record<string, PwaReviewI18nEntry[]>;
  push_enabled: number;
  push_firebase_account_id: number | null;
  push_firebase_project_id: string;
}

const AVAILABLE_LANGUAGES = [
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語' },
  { code: 'pa', label: 'Punjabi', nativeLabel: 'ਪੰਜਾਬੀ' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어' },
  { code: 'fr', label: 'French', nativeLabel: 'Français' },
  { code: 'vi', label: 'Vietnamese', nativeLabel: 'Tiếng Việt' },
  { code: 'te', label: 'Telugu', nativeLabel: 'తెలుగు' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe' },
  { code: 'ta', label: 'Tamil', nativeLabel: 'தமிழ்' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano' },
  { code: 'th', label: 'Thai', nativeLabel: 'ไทย' },
  { code: 'pl', label: 'Polish', nativeLabel: 'Polski' },
  { code: 'uk', label: 'Ukrainian', nativeLabel: 'Українська' },
  { code: 'nl', label: 'Dutch', nativeLabel: 'Nederlands' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia' },
  { code: 'ms', label: 'Malay', nativeLabel: 'Bahasa Melayu' },
  { code: 'cs', label: 'Czech', nativeLabel: 'Čeština' },
  { code: 'ro', label: 'Romanian', nativeLabel: 'Română' },
  { code: 'el', label: 'Greek', nativeLabel: 'Ελληνικά' },
  { code: 'hu', label: 'Hungarian', nativeLabel: 'Magyar' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska' },
  { code: 'he', label: 'Hebrew', nativeLabel: 'עברית' },
  { code: 'da', label: 'Danish', nativeLabel: 'Dansk' },
  { code: 'fi', label: 'Finnish', nativeLabel: 'Suomi' },
  { code: 'no', label: 'Norwegian', nativeLabel: 'Norsk' },
  { code: 'sk', label: 'Slovak', nativeLabel: 'Slovenčina' },
  { code: 'bg', label: 'Bulgarian', nativeLabel: 'Български' },
  { code: 'hr', label: 'Croatian', nativeLabel: 'Hrvatski' },
  { code: 'sr', label: 'Serbian', nativeLabel: 'Српски' },
  { code: 'sl', label: 'Slovenian', nativeLabel: 'Slovenščina' },
  { code: 'lt', label: 'Lithuanian', nativeLabel: 'Lietuvių' },
  { code: 'lv', label: 'Latvian', nativeLabel: 'Latviešu' },
  { code: 'et', label: 'Estonian', nativeLabel: 'Eesti' },
];

const initialFormData: FormData = {
  app_name: '',
  developer_name: 'Developer',
  category: 'Tools',
  rating: 4.5,
  reviews_count: 1000,
  downloads_count: '100K+',
  size: '12 MB',
  version: '1.0.0',
  short_description: '',
  full_description: '',
  icon_url: '',
  splash_url: '',
  bg_color: '#00031E',
  screenshots: [],
  videos: [],
  offer_url: '',
  proxy_url: '',
  ab_enabled: false,
  ab_group: '',
  skip_landing_pct: 0,
  status: 'active',
  store_layout: 'auto',
  enabled_languages: [],
  descriptions_i18n: {},
  fb_pixel_id: '',
  fb_access_token: '',
  fb_test_event_code: '',
  fb_events: { pageview: 'PageView', install_click: 'Lead', install_complete: 'InitiateCheckout', reg: 'CompleteRegistration', dep: 'Purchase' },
  reviews: [],
  reviews_i18n: {},
  push_enabled: 0,
  push_firebase_account_id: null,
  push_firebase_project_id: '',
};

const CATEGORIES = [
  'Tools', 'Entertainment', 'Social', 'Finance', 'Productivity',
  'Shopping', 'Communication', 'Education', 'Health & Fitness', 'Lifestyle',
  'Games', 'Music & Audio', 'Photography', 'Travel & Local', 'News & Magazines',
];

export function Pwa() {
  const queryClient = useQueryClient();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedPwa, setSelectedPwa] = useState<PwaApp | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState('');
  const [descTab, setDescTab] = useState<string>('en');
  const [translating, setTranslating] = useState(false);
  const [reviewsTranslating, setReviewsTranslating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [fbAccounts, setFbAccounts] = useState<FirebaseAccount[]>([]);

  useEffect(() => { listAccounts().then(setFbAccounts).catch(() => setFbAccounts([])); }, []);

  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings });
  const pwaMirrorBase = (settings?.pwa_mirror_base || '').replace(/\/+$/, '');
  const abTestEnabled = settings?.pwa_ab_test_enabled === '1';

  const abToggleMutation = useMutation({
    mutationFn: (next: boolean) => updateSettings({ pwa_ab_test_enabled: next ? '1' : '0' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['settings'] }),
  });

  const togglePwaAbMutation = useMutation({
    mutationFn: ({ id, ab_enabled }: { id: string; ab_enabled: boolean }) =>
      updatePwaApp(id, { ab_enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pwa-apps'] }),
  });

  const {
    data: pwaApps = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['pwa-apps'],
    queryFn: getPwaApps,
  });

  const createMutation = useMutation({
    mutationFn: (data: CreatePwaInput) => createPwaApp(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pwa-apps'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePwaInput }) =>
      updatePwaApp(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pwa-apps'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePwaApp(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pwa-apps'] });
      closeModal();
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const openCreateModal = () => {
    setFormData(initialFormData);
    setFormError('');
    setDescTab('en');
    setSelectedPwa(null);
    setModalMode('create');
  };

  const useGeneratedDraft = (draft: GeneratedPwaDraft) => {
    const generatedLanguage = draft.language_code.split('-')[0].toLowerCase();
    const localized = generatedLanguage !== 'en'
      && AVAILABLE_LANGUAGES.some((language) => language.code === generatedLanguage);
    setFormData({
      ...initialFormData,
      app_name: draft.app_name,
      developer_name: draft.developer_name,
      category: CATEGORIES.includes(draft.category) ? draft.category : 'Tools',
      rating: draft.rating,
      reviews_count: draft.reviews_count,
      downloads_count: draft.downloads_count,
      size: draft.size,
      version: draft.version,
      short_description: draft.short_description,
      full_description: draft.full_description,
      icon_url: draft.icon_url,
      splash_url: draft.splash_url,
      bg_color: draft.bg_color,
      screenshots: draft.screenshots,
      reviews: draft.reviews,
      enabled_languages: localized ? [generatedLanguage] : [],
      descriptions_i18n: localized ? {
        [generatedLanguage]: { short: draft.short_description, full: draft.full_description },
      } : {},
      reviews_i18n: localized ? {
        [generatedLanguage]: draft.reviews.map(({ text, reply }) => ({ text, ...(reply ? { reply } : {}) })),
      } : {},
      offer_url: draft.offer_url,
      store_layout: draft.store_layout,
    });
    setFormError(draft.offer_url ? '' : 'Add an Offer URL before saving. PWA projects only contain the AI prompt.');
    setDescTab(localized ? generatedLanguage : 'en');
    setSelectedPwa(null);
    setModalMode('create');
  };

  const openEditModal = (pwa: PwaApp) => {
    let screenshots: string[] = [];
    try {
      screenshots = JSON.parse(pwa.screenshots || '[]');
    } catch {
      screenshots = [];
    }
    let videos: PwaVideo[] = [];
    try {
      const raw = JSON.parse((pwa as any).videos || '[]');
      if (Array.isArray(raw)) {
        videos = raw
          .map((v: any) => {
            if (!v) return null;
            if (typeof v === 'string') return { url: v };
            if (typeof v === 'object' && typeof v.url === 'string') {
              return { url: v.url, poster: typeof v.poster === 'string' ? v.poster : undefined };
            }
            return null;
          })
          .filter((v): v is PwaVideo => !!v && !!v.url);
      }
    } catch {
      videos = [];
    }
    let descriptionsI18n: DescI18n = {};
    try {
      descriptionsI18n = JSON.parse(pwa.descriptions_i18n || '{}');
    } catch {
      descriptionsI18n = {};
    }
    const enabledLangs = Object.keys(descriptionsI18n).filter(
      k => AVAILABLE_LANGUAGES.some(l => l.code === k)
    );
    let reviews: PwaReviewItem[] = [];
    let reviewsI18n: Record<string, PwaReviewI18nEntry[]> = {};
    try {
      const cfg = JSON.parse((pwa as any).reviews_json || '{}');
      if (Array.isArray(cfg?.items)) reviews = cfg.items;
      if (cfg && typeof cfg.i18n === 'object' && cfg.i18n) reviewsI18n = cfg.i18n;
    } catch {
      reviews = [];
      reviewsI18n = {};
    }

    setFormData({
      app_name: pwa.app_name,
      developer_name: pwa.developer_name,
      category: pwa.category,
      rating: pwa.rating,
      reviews_count: pwa.reviews_count,
      downloads_count: pwa.downloads_count,
      size: pwa.size,
      version: pwa.version,
      short_description: pwa.short_description,
      full_description: pwa.full_description,
      icon_url: pwa.icon_url,
      splash_url: pwa.splash_url || '',
      bg_color: pwa.bg_color || '#00031E',
      screenshots,
      videos,
      offer_url: pwa.offer_url,
      proxy_url: pwa.proxy_url || '',
      ab_enabled: !!pwa.ab_enabled,
      ab_group: pwa.ab_group || '',
      skip_landing_pct: pwa.skip_landing_pct || 0,
      status: pwa.status as 'active' | 'paused',
      store_layout: (pwa.store_layout as 'auto' | 'play' | 'app') || 'auto',
      enabled_languages: enabledLangs,
      descriptions_i18n: descriptionsI18n,
      fb_pixel_id: pwa.fb_pixel_id || '',
      fb_access_token: pwa.fb_access_token || '',
      fb_test_event_code: pwa.fb_test_event_code || '',
      fb_events: { pageview: 'PageView', install_click: 'Lead', install_complete: 'InitiateCheckout', reg: 'CompleteRegistration', dep: 'Purchase', ...(() => { try { return JSON.parse(pwa.fb_events_json || '{}'); } catch { return {}; } })() },
      reviews,
      reviews_i18n: reviewsI18n,
      push_enabled: pwa.push_enabled ?? 0,
      push_firebase_account_id: pwa.push_firebase_account_id ?? null,
      push_firebase_project_id: pwa.push_firebase_project_id ?? '',
    });
    setFormError('');
    setDescTab('en');
    setSelectedPwa(pwa);
    setModalMode('edit');
  };

  const openDeleteModal = (pwa: PwaApp) => {
    setSelectedPwa(pwa);
    setFormError('');
    setModalMode('delete');
  };

  const openStatsModal = (pwa: PwaApp) => {
    setSelectedPwa(pwa);
    setModalMode('stats');
  };

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['pwa-stats', selectedPwa?.id],
    queryFn: () => getPwaStats(selectedPwa!.id),
    enabled: modalMode === 'stats' && !!selectedPwa,
    refetchOnWindowFocus: false,
  });

  const {
    data: variants = [],
    refetch: refetchVariants,
  } = useQuery({
    queryKey: ['pwa-variants', selectedPwa?.id],
    queryFn: () => listVariantStats(selectedPwa!.id),
    enabled: modalMode === 'edit' && !!selectedPwa,
  });

  const closeModal = () => {
    setModalMode(null);
    setSelectedPwa(null);
    setFormData(initialFormData);
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.app_name.trim()) {
      setFormError('App name is required');
      return;
    }
    if (!formData.icon_url) {
      setFormError('Icon is required');
      return;
    }
    if (!formData.offer_url.trim()) {
      setFormError('Offer URL is required');
      return;
    }
    if (formData.ab_enabled && !formData.ab_group.trim()) {
      setFormError('A/B group is required when this PWA is included in the rotation pool');
      return;
    }

    // Build i18n object with only enabled languages
    const i18nPayload: DescI18n = {};
    for (const lang of formData.enabled_languages) {
      if (formData.descriptions_i18n[lang]) {
        i18nPayload[lang] = formData.descriptions_i18n[lang];
      }
    }

    const payload = {
      app_name: formData.app_name.trim(),
      developer_name: formData.developer_name.trim(),
      category: formData.category,
      rating: formData.rating,
      reviews_count: formData.reviews_count,
      downloads_count: formData.downloads_count,
      size: formData.size,
      version: formData.version,
      short_description: formData.short_description.trim(),
      full_description: formData.full_description.trim(),
      icon_url: formData.icon_url,
      splash_url: formData.splash_url,
      bg_color: formData.bg_color,
      screenshots: JSON.stringify(formData.screenshots),
      videos: JSON.stringify(
        formData.videos
          .filter((v) => v && v.url)
          .map((v) => ({ url: v.url, ...(v.poster ? { poster: v.poster } : {}) }))
      ),
      offer_url: formData.offer_url.trim(),
      proxy_url: formData.proxy_url.trim() || null,
      ab_enabled: formData.ab_enabled,
      ab_group: formData.ab_group.trim() || null,
      skip_landing_pct: Math.max(0, Math.min(100, Math.round(formData.skip_landing_pct || 0))),
      descriptions_i18n: JSON.stringify(i18nPayload),
      status: formData.status,
      store_layout: formData.store_layout,
      fb_pixel_id: formData.fb_pixel_id.trim() || null,
      fb_access_token: formData.fb_access_token.trim() || null,
      fb_test_event_code: formData.fb_test_event_code.trim() || null,
      fb_events_json: JSON.stringify(formData.fb_events),
      reviews_json: JSON.stringify({ items: formData.reviews, i18n: formData.reviews_i18n }),
      push_enabled: formData.push_enabled,
      push_firebase_account_id: formData.push_firebase_account_id,
      push_firebase_project_id: formData.push_firebase_project_id.trim() || null,
    };

    if (modalMode === 'create') {
      createMutation.mutate(payload);
    } else if (modalMode === 'edit' && selectedPwa) {
      updateMutation.mutate({ id: selectedPwa.id, data: payload });
    }
  };

  const handleDelete = () => {
    if (selectedPwa) {
      deleteMutation.mutate(selectedPwa.id);
    }
  };

  const handleIconUpload = async (file: File): Promise<string> => {
    return uploadPwaMedia(file);
  };

  const handleScreenshotUpload = async (file: File) => {
    try {
      const url = await uploadPwaMedia(file);
      setFormData((prev) => ({
        ...prev,
        screenshots: [...prev.screenshots, url],
      }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Upload failed');
    }
  };

  const handleVideoUpload = async (file: File) => {
    try {
      const url = await uploadPwaMedia(file);
      setFormData((prev) => ({
        ...prev,
        videos: [...prev.videos, { url }],
      }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Video upload failed');
    }
  };

  const handleVideoPosterUpload = async (index: number, file: File) => {
    try {
      const url = await uploadPwaMedia(file);
      setFormData((prev) => ({
        ...prev,
        videos: prev.videos.map((v, i) => (i === index ? { ...v, poster: url } : v)),
      }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Poster upload failed');
    }
  };

  const removeVideo = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index),
    }));
  };

  const clearVideoPoster = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      videos: prev.videos.map((v, i) => (i === index ? { url: v.url } : v)),
    }));
  };

  const handleAutoTranslate = async () => {
    if (!formData.short_description && !formData.full_description) {
      setFormError('Write English descriptions first before translating');
      return;
    }
    if (formData.enabled_languages.length === 0) {
      setFormError('Enable at least one language to translate to');
      return;
    }
    setTranslating(true);
    setFormError('');
    try {
      const result = await translateDescriptions(
        formData.short_description,
        formData.full_description,
        formData.enabled_languages
      );
      setFormData((prev) => ({
        ...prev,
        descriptions_i18n: { ...prev.descriptions_i18n, ...result },
      }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const handleReviewsChange = (items: PwaReviewItem[]) => {
    // Editing items invalidates existing translations; clear them.
    setFormData((prev) => ({ ...prev, reviews: items, reviews_i18n: {} }));
  };

  const handleTranslateReviews = async () => {
    if (formData.reviews.length === 0) {
      setFormError('Add at least one review before translating');
      return;
    }
    const targetLangs = AVAILABLE_LANGUAGES.map((l) => l.code);
    setReviewsTranslating(true);
    setFormError('');
    try {
      const result = await translateReviews(
        formData.reviews.map((r) => ({ text: r.text, reply: r.reply || '' })),
        targetLangs
      );
      setFormData((prev) => ({ ...prev, reviews_i18n: result }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Review translation failed');
    } finally {
      setReviewsTranslating(false);
    }
  };

  const toggleLanguage = (langCode: string) => {
    setFormData((prev) => {
      const enabled = prev.enabled_languages.includes(langCode)
        ? prev.enabled_languages.filter((l) => l !== langCode)
        : [...prev.enabled_languages, langCode];
      // If we're removing the language we're currently viewing, switch to English
      if (!enabled.includes(descTab) && descTab !== 'en') {
        setDescTab('en');
      }
      return { ...prev, enabled_languages: enabled };
    });
  };

  const removeScreenshot = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index),
    }));
  };

  const getPwaUrl = (id: string) => {
    const base = import.meta.env.VITE_PWA_BASE_URL || 'https://newads-api.newoneads.workers.dev';
    return `${base}/pwa/${id}`;
  };

  const copyLink = async (id: string) => {
    const url = getPwaUrl(id);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.top = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('execCommand failed');
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
    } catch (err) {
      window.prompt('Copy this link:', url);
    }
  };

  // Stats totals across all PWAs
  const totalStats = pwaApps.reduce(
    (acc, p) => ({
      visits: acc.visits + (p.total_page_visits || 0),
      clicks: acc.clicks + (p.total_install_clicks || 0),
      installs: acc.installs + (p.total_install_completes || 0),
      opens: acc.opens + (p.total_pwa_opens || 0),
    }),
    { visits: 0, clicks: 0, installs: 0, opens: 0 }
  );

  const columns = [
    {
      key: 'icon',
      label: '',
      render: (pwa: PwaApp) => (
        <img
          src={pwa.icon_url}
          alt=""
          className="w-10 h-10 rounded-lg object-cover"
        />
      ),
    },
    {
      key: 'app_name',
      label: 'Name',
      render: (pwa: PwaApp) => (
        <div>
          <span className="font-medium text-content">{pwa.app_name}</span>
          <p className="text-xs text-content-subtle">{pwa.developer_name}</p>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      render: (pwa: PwaApp) => (
        <span className="text-sm text-content-muted">{pwa.category}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (pwa: PwaApp) => {
        const styles: Record<string, string> = {
          active: 'bg-success-soft text-success',
          paused: 'bg-surface-muted text-content-muted',
        };
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[pwa.status] || ''}`}>
            {pwa.status.charAt(0).toUpperCase() + pwa.status.slice(1)}
          </span>
        );
      },
    },
    {
      key: 'ab_enabled',
      label: 'A/B',
      render: (pwa: PwaApp) => (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!pwa.ab_enabled}
            onChange={(e) => {
              e.stopPropagation();
              if (e.target.checked && !pwa.ab_group) {
                alert('Set an A/B group on this PWA before adding it to the rotation pool. Open the PWA to assign one.');
                return;
              }
              togglePwaAbMutation.mutate({ id: pwa.id, ab_enabled: e.target.checked });
            }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary cursor-pointer"
            title={pwa.ab_group ? 'Include in A/B rotation pool' : 'Assign an A/B group first'}
          />
          {pwa.ab_group ? (
            <span
              className="px-1.5 py-0.5 rounded bg-info-soft text-info text-[11px] font-medium"
              title="A/B group"
            >
              {pwa.ab_group}
            </span>
          ) : (
            <span className="text-[11px] text-content-subtle" title="No group assigned">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'skip_landing_pct',
      label: 'Skip %',
      render: (pwa: PwaApp) => (
        <span
          className={`text-sm ${pwa.skip_landing_pct ? 'text-content font-medium' : 'text-content-subtle'}`}
          title="Percentage of visitors redirected to the offer URL instead of the landing"
        >
          {pwa.skip_landing_pct || 0}%
        </span>
      ),
    },
    {
      key: 'visits',
      label: 'Visits',
      render: (pwa: PwaApp) => (
        <span className="text-sm text-content-muted">{pwa.total_page_visits || 0}</span>
      ),
    },
    {
      key: 'installs',
      label: 'Installs',
      render: (pwa: PwaApp) => (
        <span className="text-sm text-content-muted">{pwa.total_install_completes || 0}</span>
      ),
    },
    {
      key: 'link',
      label: 'Link',
      render: (pwa: PwaApp) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            copyLink(pwa.id);
          }}
          className="text-primary hover:text-primary/80 text-sm font-medium flex items-center gap-1"
          title="Copy link"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          {copiedId === pwa.id ? 'Copied' : 'Copy'}
        </button>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (pwa: PwaApp) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openStatsModal(pwa);
            }}
            className="text-content-subtle hover:text-primary transition-colors"
            title="Analytics"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3v18h18M7 15l4-4 4 4 6-6" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(pwa);
            }}
            className="text-content-subtle hover:text-content-muted transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(pwa);
            }}
            className="text-content-subtle hover:text-danger transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="bg-danger-soft border border-danger/30 rounded-lg p-4">
        <p className="text-sm text-danger">
          Failed to load PWA apps:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">PWA</h1>
          <p className="text-sm text-content-subtle mt-1">
            Manage PWA applications with Play Store landing pages
          </p>
        </div>
        <div className="flex items-center gap-4">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={abTestEnabled}
              disabled={abToggleMutation.isPending}
              onChange={(e) => abToggleMutation.mutate(e.target.checked)}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-content-muted">A/B test</span>
            <span className="text-xs text-content-subtle">
              {abTestEnabled ? 'rotating within each group on each impression' : 'off — ad uses its own PWA'}
            </span>
          </label>
          <EditOnly>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={openCreateModal}>Create manually</Button>
              <Button onClick={() => setModalMode('generate')}>
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M12 3l1.3 4.1L17 9l-3.7 1.9L12 15l-1.3-4.1L7 9l3.7-1.9L12 3zM5 14l.8 2.2L8 17l-2.2.8L5 20l-.8-2.2L2 17l2.2-.8L5 14zM19 13l.8 2.2L22 16l-2.2.8L19 19l-.8-2.2L16 16l2.2-.8L19 13z" />
                </svg>
                Generate PWA
              </Button>
            </div>
          </EditOnly>
        </div>
      </div>

      {/* Stats Cards */}
      {pwaApps.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-sm text-content-subtle">Total Visits</p>
            <p className="text-xl sm:text-2xl font-semibold text-content">{totalStats.visits.toLocaleString()}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-sm text-content-subtle">Install Clicks</p>
            <p className="text-xl sm:text-2xl font-semibold text-content">{totalStats.clicks.toLocaleString()}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-sm text-content-subtle">Installs</p>
            <p className="text-xl sm:text-2xl font-semibold text-content">{totalStats.installs.toLocaleString()}</p>
          </div>
          <div className="bg-surface border border-border rounded-lg p-4">
            <p className="text-sm text-content-subtle">PWA Opens</p>
            <p className="text-xl sm:text-2xl font-semibold text-content">{totalStats.opens.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={pwaApps}
        loading={isLoading}
        onRowClick={openEditModal}
        keyExtractor={(pwa) => pwa.id}
      />

      <PwaGeneratorModal
        isOpen={modalMode === 'generate'}
        onClose={closeModal}
        onUseDraft={useGeneratedDraft}
      />

      {/* Create/Edit Modal - Fullscreen compact layout */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Create PWA' : 'Edit PWA'}
        size="full"
      >
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          {formError && (
            <div className="p-2 bg-danger-soft border border-danger/30 rounded-md mb-3 flex-shrink-0">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: App Settings */}
            <div className="space-y-3">
              {/* App Name + Developer */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="app_name" className="block text-xs font-medium text-content-subtle mb-1">App Name *</label>
                  <input
                    type="text"
                    id="app_name"
                    value={formData.app_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, app_name: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="My App"
                  />
                </div>
                <div>
                  <label htmlFor="developer_name" className="block text-xs font-medium text-content-subtle mb-1">Developer</label>
                  <input
                    type="text"
                    id="developer_name"
                    value={formData.developer_name}
                    onChange={(e) => setFormData((prev) => ({ ...prev, developer_name: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="Developer"
                  />
                </div>
              </div>

              {/* Category + Status */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Category</label>
                  <Select
                    value={formData.category}
                    onChange={(v) => setFormData((prev) => ({ ...prev, category: v }))}
                    searchable
                    size="sm"
                    options={CATEGORIES.map((cat) => ({ value: cat, label: cat }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-content-subtle mb-1">Status</label>
                  <Select
                    value={formData.status}
                    onChange={(v) => setFormData((prev) => ({ ...prev, status: v as 'active' | 'paused' }))}
                    size="sm"
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'paused', label: 'Paused' },
                    ]}
                  />
                </div>
              </div>

              {/* Store layout */}
              <div>
                <label className="block text-xs font-medium text-content-subtle mb-1">Store layout</label>
                <Select
                  value={formData.store_layout}
                  onChange={(v) => setFormData((prev) => ({ ...prev, store_layout: v as 'auto' | 'play' | 'app' }))}
                  size="sm"
                  options={[
                    { value: 'auto', label: 'Auto (by device)' },
                    { value: 'play', label: 'Play (Google Play)' },
                    { value: 'app', label: 'App (App Store)' },
                  ]}
                />
              </div>

              {/* Offer URL */}
              <div>
                <label htmlFor="offer_url" className="block text-xs font-medium text-content-subtle mb-1">Offer URL *</label>
                <div className="flex items-start gap-2">
                  <input
                    type="url"
                    id="offer_url"
                    value={formData.offer_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, offer_url: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="https://example.com/offer"
                  />
                  <div className="w-56 shrink-0">
                    <TrackerLinkPicker onPick={(url) => setFormData((prev) => ({ ...prev, offer_url: url }))} />
                  </div>
                </div>
              </div>

              {/* Proxy URL (fallback when SDK routes via proxy) */}
              <div>
                <label htmlFor="proxy_url" className="block text-xs font-medium text-content-subtle mb-1">Proxy URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    id="proxy_url"
                    value={formData.proxy_url}
                    onChange={(e) => setFormData((prev) => ({ ...prev, proxy_url: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="https://example.com/direct-offer"
                  />
                  <div className="w-56 shrink-0">
                    <TrackerLinkPicker onPick={(url) => setFormData((prev) => ({ ...prev, proxy_url: url }))} />
                  </div>
                  {pwaMirrorBase && selectedPwa && (
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, proxy_url: `${pwaMirrorBase}/pwa/${selectedPwa.id}` }))}
                      className="whitespace-nowrap px-2.5 py-1.5 text-xs font-medium text-primary border border-primary rounded-md hover:bg-primary/5"
                    >
                      Use mirror
                    </button>
                  )}
                </div>
                <p className="mt-1 text-[11px] text-content-subtle">Served instead of the PWA landing when SDK request arrives via proxy (blocked geos).</p>
              </div>

              {/* A/B test pool */}
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ab_enabled}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ab_enabled: e.target.checked }))}
                    className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                  />
                  <span className="text-xs font-medium text-content-muted">Include in A/B rotation pool</span>
                </label>
                <div className="mt-2">
                  <label htmlFor="ab_group" className="block text-xs font-medium text-content-subtle mb-1">
                    A/B group {formData.ab_enabled && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    id="ab_group"
                    value={formData.ab_group}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ab_group: e.target.value }))}
                    placeholder="e.g. alpha"
                    className="block w-48 px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
                <p className="mt-1 text-[11px] text-content-subtle">Rotation happens only between PWAs sharing the same group label. Required when "Include in A/B rotation pool" is on.</p>
              </div>

              {/* Skip landing split */}
              <div>
                <label htmlFor="skip_landing_pct" className="block text-xs font-medium text-content-subtle mb-1">
                  Skip landing % <span className="text-content-subtle font-normal">(0 = always show, 100 = always redirect)</span>
                </label>
                <input
                  type="number"
                  id="skip_landing_pct"
                  min={0}
                  max={100}
                  step={1}
                  value={formData.skip_landing_pct}
                  onChange={(e) => setFormData((prev) => ({ ...prev, skip_landing_pct: parseInt(e.target.value) || 0 }))}
                  className="block w-32 px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
                <p className="mt-1 text-[11px] text-content-subtle">Portion of visitors redirected straight to the offer URL instead of seeing the landing. Sticky per device (click_id / 30d cookie).</p>
              </div>

              {/* Rating + Reviews */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="rating" className="block text-xs font-medium text-content-subtle mb-1">Rating</label>
                  <input
                    type="number"
                    id="rating"
                    value={formData.rating}
                    onChange={(e) => setFormData((prev) => ({ ...prev, rating: parseFloat(e.target.value) || 0 }))}
                    min={1}
                    max={5}
                    step={0.1}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
                <div>
                  <label htmlFor="reviews_count" className="block text-xs font-medium text-content-subtle mb-1">Reviews</label>
                  <input
                    type="number"
                    id="reviews_count"
                    value={formData.reviews_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, reviews_count: parseInt(e.target.value) || 0 }))}
                    min={0}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Downloads + Size */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="downloads_count" className="block text-xs font-medium text-content-subtle mb-1">Downloads</label>
                  <input
                    type="text"
                    id="downloads_count"
                    value={formData.downloads_count}
                    onChange={(e) => setFormData((prev) => ({ ...prev, downloads_count: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="100K+"
                  />
                </div>
                <div>
                  <label htmlFor="size" className="block text-xs font-medium text-content-subtle mb-1">Size</label>
                  <input
                    type="text"
                    id="size"
                    value={formData.size}
                    onChange={(e) => setFormData((prev) => ({ ...prev, size: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="12 MB"
                  />
                </div>
              </div>

              {/* Version + BG Color */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="version" className="block text-xs font-medium text-content-subtle mb-1">Version</label>
                  <input
                    type="text"
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData((prev) => ({ ...prev, version: e.target.value }))}
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="1.0.0"
                  />
                </div>
                <div>
                  <label htmlFor="bg_color" className="block text-xs font-medium text-content-subtle mb-1">BG Color</label>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="color"
                      id="bg_color"
                      value={formData.bg_color}
                      onChange={(e) => setFormData((prev) => ({ ...prev, bg_color: e.target.value }))}
                      className="w-8 h-8 rounded border border-border-strong cursor-pointer p-0.5 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={formData.bg_color}
                      onChange={(e) => setFormData((prev) => ({ ...prev, bg_color: e.target.value }))}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="#00031E"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Middle: Media */}
            <div className="space-y-3">
              {/* Icon */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">Icon *</label>
                  <span className="text-xs text-content-subtle">512x512 PNG</span>
                </div>
                <MediaUpload
                  value={formData.icon_url}
                  onChange={(url) => setFormData((prev) => ({ ...prev, icon_url: url }))}
                  onUpload={handleIconUpload}
                  accept="image/*"
                  mediaType="image"
                />
              </div>

              {/* Splash Screen */}
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">Splash Screen</label>
                  <span className="text-xs text-content-subtle">Full-screen launch image</span>
                </div>
                <MediaUpload
                  value={formData.splash_url}
                  onChange={(url) => setFormData((prev) => ({ ...prev, splash_url: url }))}
                  onUpload={handleIconUpload}
                  accept="image/*"
                  mediaType="image"
                />
              </div>

              {/* Videos */}
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">
                    Videos
                    <span className="text-content-subtle font-normal ml-1">(Google Play-style)</span>
                  </label>
                  <label className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary border border-primary/30 rounded-md cursor-pointer hover:bg-primary/5 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add video
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleVideoUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                {formData.videos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {formData.videos.map((v, i) => (
                      <div
                        key={i}
                        className="relative group rounded-md overflow-hidden border border-border bg-black"
                        style={{ aspectRatio: '16 / 9' }}
                      >
                        {v.poster ? (
                          <img src={v.poster} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <video src={v.url} className="w-full h-full object-cover" muted preload="metadata" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="w-9 h-9 rounded-full bg-surface/95 flex items-center justify-center shadow">
                            <svg className="w-4 h-4 text-content ml-0.5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          </span>
                        </div>
                        <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <label
                            className="w-5 h-5 bg-surface text-content-muted rounded-full flex items-center justify-center text-[10px] cursor-pointer hover:bg-surface-muted"
                            title={v.poster ? 'Replace poster' : 'Set poster'}
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M4 6h16v12H4V6z" />
                            </svg>
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleVideoPosterUpload(i, file);
                                e.target.value = '';
                              }}
                            />
                          </label>
                          {v.poster && (
                            <button
                              type="button"
                              onClick={() => clearVideoPoster(i)}
                              className="w-5 h-5 bg-surface text-content-muted rounded-full flex items-center justify-center text-[10px] hover:bg-surface-muted"
                              title="Clear poster"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeVideo(i)}
                            className="w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center text-xs hover:bg-danger"
                            title="Remove video"
                          >
                            &times;
                          </button>
                        </div>
                        <div className="absolute left-1 bottom-1 px-1.5 py-0.5 rounded bg-black/55 text-[10px] text-white font-medium">
                          {v.poster ? 'Trailer' : 'No poster'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border rounded-lg p-4 text-center">
                    <p className="text-xs text-content-subtle">No videos added — upload to display a Play-style trailer card</p>
                  </div>
                )}
              </div>

              {/* Screenshots */}
              <div className="pt-2 border-t border-border">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-content-subtle">Screenshots</label>
                  <label className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary border border-primary/30 rounded-md cursor-pointer hover:bg-primary/5 transition-colors">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleScreenshotUpload(file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>

                {formData.screenshots.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {formData.screenshots.map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={url} alt="" className="w-full h-28 rounded-md object-cover" />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(i)}
                          className="absolute top-1 right-1 w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-danger"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <p className="text-xs text-content-subtle">No screenshots added</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Descriptions & i18n */}
            <div className="space-y-3 min-h-0 flex flex-col">
              {/* Language checkboxes */}
              <div>
                <label className="text-xs font-medium text-content-subtle mb-1 block">Languages</label>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 max-h-32 overflow-y-auto border border-border rounded-md p-2">
                  {AVAILABLE_LANGUAGES.map((lang) => (
                    <label key={lang.code} className="flex items-center gap-1.5 text-xs text-content-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.enabled_languages.includes(lang.code)}
                        onChange={() => toggleLanguage(lang.code)}
                        className="rounded border-border-strong text-primary focus:ring-primary w-3.5 h-3.5"
                      />
                      {lang.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Language tabs */}
              <div className="flex flex-wrap gap-1 border-b border-border pb-1">
                <button
                  type="button"
                  onClick={() => setDescTab('en')}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    descTab === 'en'
                      ? 'bg-primary/10 text-primary'
                      : 'text-content-subtle hover:text-content-muted hover:bg-surface-muted'
                  }`}
                >
                  EN
                </button>
                {formData.enabled_languages.map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setDescTab(code)}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                        descTab === code
                          ? 'bg-primary/10 text-primary'
                          : 'text-content-subtle hover:text-content-muted hover:bg-surface-muted'
                      }`}
                    >
                      {code.toUpperCase()}
                    </button>
                ))}
              </div>

              {/* English descriptions */}
              {descTab === 'en' && (
                <div className="space-y-2 flex-1 flex flex-col">
                  <div>
                    <label htmlFor="short_description" className="block text-xs font-medium text-content-subtle mb-1">Short Description</label>
                    <textarea
                      id="short_description"
                      value={formData.short_description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, short_description: e.target.value }))}
                      rows={2}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Brief description of the app"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label htmlFor="full_description" className="block text-xs font-medium text-content-subtle mb-1">Full Description</label>
                    <textarea
                      id="full_description"
                      value={formData.full_description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, full_description: e.target.value }))}
                      rows={8}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary flex-1"
                      placeholder="Detailed description"
                    />
                  </div>
                </div>
              )}

              {/* Non-English descriptions */}
              {descTab !== 'en' && (
                <div className="space-y-2 flex-1 flex flex-col">
                  <div>
                    <label className="block text-xs font-medium text-content-subtle mb-1">Short Description</label>
                    <textarea
                      value={formData.descriptions_i18n[descTab]?.short || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          descriptions_i18n: {
                            ...prev.descriptions_i18n,
                            [descTab]: {
                              ...prev.descriptions_i18n[descTab],
                              short: e.target.value,
                            },
                          },
                        }))
                      }
                      rows={2}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                      placeholder="Translated short description"
                    />
                  </div>
                  <div className="flex-1 flex flex-col">
                    <label className="block text-xs font-medium text-content-subtle mb-1">Full Description</label>
                    <textarea
                      value={formData.descriptions_i18n[descTab]?.full || ''}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          descriptions_i18n: {
                            ...prev.descriptions_i18n,
                            [descTab]: {
                              ...prev.descriptions_i18n[descTab],
                              full: e.target.value,
                            },
                          },
                        }))
                      }
                      rows={8}
                      className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary flex-1"
                      placeholder="Translated full description"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAutoTranslate}
                    disabled={translating}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 rounded-md hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors self-start"
                  >
                    {translating ? (
                      <>
                        <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Translating...
                      </>
                    ) : (
                      'Auto-translate from English'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reviews */}
          <ReviewsEditor
            items={formData.reviews}
            i18n={formData.reviews_i18n}
            onItemsChange={handleReviewsChange}
            onTranslate={handleTranslateReviews}
            translating={reviewsTranslating}
            uploadPhoto={uploadPwaMedia}
          />

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-semibold mb-3">Facebook Pixel</h3>
            <label className="block text-xs font-medium text-content-subtle mb-1">Pixel ID</label>
            <input
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              value={formData.fb_pixel_id}
              onChange={(e) => setFormData((prev) => ({ ...prev, fb_pixel_id: e.target.value }))}
              placeholder="e.g. 1234567890"
            />
            <label className="block text-xs font-medium text-content-subtle mb-1">Access Token (Conversions API)</label>
            <input
              type="password"
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              value={formData.fb_access_token}
              onChange={(e) => setFormData((prev) => ({ ...prev, fb_access_token: e.target.value }))}
              placeholder="EAAB..."
              autoComplete="off"
            />
            <label className="block text-xs font-medium text-content-subtle mb-1">Test Event Code (optional)</label>
            <input
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              value={formData.fb_test_event_code}
              onChange={(e) => setFormData((prev) => ({ ...prev, fb_test_event_code: e.target.value }))}
              placeholder="TEST12345"
            />
            <div className="grid grid-cols-2 gap-3">
              {(['pageview', 'install_click', 'install_complete', 'reg', 'dep'] as const).map((k) => (
                <div key={k}>
                  <label className="block text-xs text-gray-500 mb-1">{k} event name</label>
                  <input
                    className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                    value={formData.fb_events[k] ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, fb_events: { ...prev.fb_events, [k]: e.target.value } }))}
                    placeholder="(empty = off)"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <h3 className="text-sm font-semibold mb-3">Push notifications</h3>
            <label className="inline-flex items-center gap-2 cursor-pointer select-none mb-3">
              <input
                type="checkbox"
                checked={!!formData.push_enabled}
                onChange={(e) => setFormData((prev) => ({ ...prev, push_enabled: e.target.checked ? 1 : 0 }))}
              />
              <span className="text-sm">Enable web push for installed PWAs</span>
            </label>
            <label className="block text-xs font-medium text-content-subtle mb-1">Firebase account</label>
            <select
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3 bg-white dark:bg-gray-800"
              value={formData.push_firebase_account_id ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, push_firebase_account_id: e.target.value ? Number(e.target.value) : null }))}
            >
              <option value="">— none —</option>
              {fbAccounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            <label className="block text-xs font-medium text-content-subtle mb-1">Firebase project id (for sending)</label>
            <input
              className="block w-full px-2.5 py-1.5 border border-border-strong rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary mb-3"
              type="text"
              value={formData.push_firebase_project_id}
              placeholder="my-firebase-project"
              onChange={(e) => setFormData((prev) => ({ ...prev, push_firebase_project_id: e.target.value }))}
            />
            <p className="text-xs text-content-subtle">The selected Firebase account must have web credentials (API key, sender id, app id, VAPID key) configured on the Firebase page.</p>
          </div>

          {modalMode === 'edit' && selectedPwa && (
            <section className="mt-6 border-t border-border pt-4">
              <h3 className="text-sm font-semibold mb-2">Variants (A/B)</h3>
              <p className="text-xs text-content-subtle mb-3">
                Each visitor sees one variant, sticky per click_id. Traffic share = weight / sum(weights of enabled).
              </p>

              <div className="space-y-2">
                {variants.map((v) => (
                  <VariantRow
                    key={v.id}
                    pwaId={selectedPwa.id}
                    variant={v}
                    totalWeight={variants
                      .filter((x) => x.enabled === 1 && x.weight > 0)
                      .reduce((acc, x) => acc + x.weight, 0)}
                    onChanged={refetchVariants}
                  />
                ))}
              </div>

              <Button
                type="button"
                variant="secondary"
                className="mt-3"
                onClick={async () => {
                  const base = variants[0];
                  await createVariant(selectedPwa.id, {
                    app_name: base?.app_name ?? formData.app_name,
                    developer_name: base?.developer_name ?? formData.developer_name,
                    icon_url: base?.icon_url ?? formData.icon_url,
                    weight: 50,
                    enabled: true,
                  });
                  refetchVariants();
                }}
              >
                Add variant
              </Button>
            </section>
          )}
          </div>

          <div className="flex justify-end gap-3 pt-3 mt-3 border-t border-border flex-shrink-0">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {modalMode === 'create' ? 'Create PWA' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete PWA"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <p className="text-sm text-content-muted">
            Are you sure you want to delete <strong>{selectedPwa?.app_name}</strong>? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Stats Modal */}
      <Modal
        isOpen={modalMode === 'stats'}
        onClose={closeModal}
        title={selectedPwa ? `Analytics — ${selectedPwa.app_name}` : 'Analytics'}
        size="xl"
      >
        <PwaStatsView loading={statsLoading} stats={statsData} />
      </Modal>
    </div>
  );
}

function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  const m = Math.floor(s / 60);
  const rs = Math.round(s - m * 60);
  return `${m}m ${rs}s`;
}

function pct(num: number, denom: number): string {
  if (!denom) return '—';
  return `${Math.round((num / denom) * 100)}%`;
}

interface StatsViewProps {
  loading: boolean;
  stats: PwaStatsResponse | undefined;
}

function PwaStatsView({ loading, stats }: StatsViewProps) {
  if (loading || !stats) {
    return <div className="p-6 text-sm text-content-subtle">Loading analytics…</div>;
  }

  const totals = stats.totals;
  const eng = stats.engagement || ({} as PwaEngagementStats);
  const dist = stats.scroll_distribution || ({} as PwaScrollDistribution);
  const sessions = eng.sessions || 0;
  const visits = totals.page_visits || 0;
  const clicks = totals.install_clicks || 0;

  const funnelRows = [
    { label: 'Page visits', value: visits, of: visits },
    { label: 'Scrolled past reviews', value: eng.reached_reviews_count || 0, of: sessions || visits },
    { label: 'Scrolled to bottom', value: eng.reached_bottom_count || 0, of: sessions || visits },
    { label: 'Install clicks', value: clicks, of: visits },
    { label: 'Installs completed', value: totals.install_completes || 0, of: clicks || visits },
    { label: 'PWA opens', value: totals.pwa_opens || 0, of: visits },
  ];

  const bars = [
    { label: '0–25%', value: dist.b0_25 || 0 },
    { label: '25–50%', value: dist.b25_50 || 0 },
    { label: '50–75%', value: dist.b50_75 || 0 },
    { label: '75–99%', value: dist.b75_100 || 0 },
    { label: '100%', value: dist.b100 || 0 },
  ];
  const barMax = Math.max(1, ...bars.map((b) => b.value));

  const kpis = [
    { label: 'Avg time on page', value: formatMs(eng.avg_time_on_page_ms), hint: 'from load to exit' },
    { label: 'Avg active time', value: formatMs(eng.avg_active_time_ms), hint: 'tab visible' },
    { label: 'Time to install click', value: formatMs(eng.avg_dwell_before_click_ms), hint: 'avg dwell before CTA' },
    { label: 'Avg scroll depth', value: eng.avg_scroll_pct != null ? `${Math.round(eng.avg_scroll_pct)}%` : '—', hint: 'how far users scroll' },
    { label: 'Bounce rate', value: pct(eng.bounces || 0, sessions), hint: 'sessions with <10% scroll' },
    { label: 'Reviews seen', value: pct(eng.reached_reviews_count || 0, sessions || visits), hint: 'reviews block entered viewport' },
    { label: 'Expanded description', value: pct(eng.read_more_count || 0, sessions), hint: 'clicked "Show more"' },
    { label: 'Swiped screenshots', value: pct(eng.screenshot_interacted_count || 0, sessions), hint: 'horizontal scroll in gallery' },
  ];

  const totalDevices = (stats.devices || []).reduce((s, d) => s + (d.count || 0), 0);

  return (
    <div className="space-y-6">
      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="bg-surface-muted border border-border rounded-lg p-3">
            <p className="text-xs text-content-subtle">{k.label}</p>
            <p className="text-xl font-semibold text-content mt-1">{k.value}</p>
            <p className="text-xs text-content-subtle mt-1">{k.hint}</p>
          </div>
        ))}
      </div>

      {sessions === 0 && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft p-3 text-sm text-warning">
          Engagement data not collected yet — metrics appear once real users start visiting this PWA.
        </div>
      )}

      {/* Funnel */}
      <div>
        <h4 className="text-sm font-semibold text-content mb-2">Funnel</h4>
        <div className="space-y-1.5">
          {funnelRows.map((row) => {
            const ratio = row.of > 0 ? Math.min(1, row.value / row.of) : 0;
            return (
              <div key={row.label} className="flex items-center gap-3">
                <div className="w-44 text-xs text-content-muted">{row.label}</div>
                <div className="flex-1 bg-surface-muted rounded h-5 relative overflow-hidden">
                  <div
                    className="bg-primary/80 h-full"
                    style={{ width: `${Math.round(ratio * 100)}%` }}
                  />
                </div>
                <div className="w-32 text-right text-xs text-content-muted tabular-nums">
                  {row.value.toLocaleString()}{' '}
                  <span className="text-content-subtle">({pct(row.value, row.of)})</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scroll depth distribution */}
      <div>
        <h4 className="text-sm font-semibold text-content mb-2">Scroll depth distribution</h4>
        {sessions === 0 ? (
          <p className="text-xs text-content-subtle">No sessions recorded yet.</p>
        ) : (
          <div className="flex items-end gap-2 h-32">
            {bars.map((b) => (
              <div key={b.label} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full flex-1 flex items-end">
                  <div
                    className="w-full bg-primary/70 rounded-t"
                    style={{ height: `${(b.value / barMax) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-content-muted tabular-nums">{b.value}</div>
                <div className="text-xs text-content-subtle">{b.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Devices */}
      {totalDevices > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-content mb-2">Devices</h4>
          <div className="flex gap-2 flex-wrap">
            {stats.devices.map((d) => (
              <div key={d.device_type} className="px-3 py-1.5 bg-surface-muted rounded text-xs text-content-muted">
                <span className="font-medium capitalize">{d.device_type}</span>{' '}
                <span className="text-content-subtle">
                  {d.count.toLocaleString()} ({pct(d.count, totalDevices)})
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VariantRow({
  pwaId,
  variant,
  totalWeight,
  onChanged,
}: {
  pwaId: string;
  variant: PwaVariantStats;
  totalWeight: number;
  onChanged: () => void;
}) {
  const [appName, setAppName] = useState(variant.app_name);
  const [devName, setDevName] = useState(variant.developer_name);
  const [iconUrl, setIconUrl] = useState(variant.icon_url);
  const [weight, setWeight] = useState(variant.weight);
  const [enabled, setEnabled] = useState(variant.enabled === 1);

  const share =
    enabled && weight > 0 && totalWeight > 0
      ? Math.round((weight / totalWeight) * 1000) / 10
      : 0;

  const save = async () => {
    await updateVariant(pwaId, variant.id, {
      app_name: appName,
      developer_name: devName,
      icon_url: iconUrl,
      weight,
      enabled,
    });
    onChanged();
  };

  return (
    <div className="flex items-center gap-2 p-2 border border-border rounded">
      <img src={iconUrl} alt="" className="w-10 h-10 rounded flex-shrink-0" />
      <input
        value={appName}
        onChange={(e) => setAppName(e.target.value)}
        className="px-2 py-1 border border-border-strong rounded text-sm flex-1 min-w-0"
        placeholder="app_name"
      />
      <input
        value={devName}
        onChange={(e) => setDevName(e.target.value)}
        className="px-2 py-1 border border-border-strong rounded text-sm w-40 flex-shrink-0"
        placeholder="developer_name"
      />
      <input
        value={iconUrl}
        onChange={(e) => setIconUrl(e.target.value)}
        className="px-2 py-1 border border-border-strong rounded text-sm w-64 flex-shrink-0"
        placeholder="icon_url"
      />
      <input
        type="number"
        min={0}
        max={100}
        value={weight}
        onChange={(e) => setWeight(Number(e.target.value))}
        className="w-16 px-2 py-1 border border-border-strong rounded text-sm flex-shrink-0"
        title="weight (0-100)"
      />
      <span className="text-xs text-content-subtle w-12 text-right flex-shrink-0">{share}%</span>
      <label className="text-xs flex items-center gap-1 flex-shrink-0">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        on
      </label>
      <span className="text-xs w-16 text-right flex-shrink-0">{variant.impressions} imp</span>
      <span className="text-xs w-16 text-right flex-shrink-0">{variant.installs} inst</span>
      <span className="text-xs w-16 text-right flex-shrink-0">{(variant.cr * 100).toFixed(2)}%</span>
      <Button type="button" size="sm" variant="secondary" onClick={save}>
        Save
      </Button>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={async () => {
          if (!confirm('Promote this variant as the new default and delete the others?')) return;
          await promoteVariant(pwaId, variant.id);
          onChanged();
        }}
      >
        Promote
      </Button>
      <Button
        type="button"
        size="sm"
        variant="danger"
        onClick={async () => {
          if (!confirm('Delete this variant? In-flight events referencing it stay in the DB but will not join.')) return;
          await deleteVariant(pwaId, variant.id);
          onChanged();
        }}
      >
        Delete
      </Button>
    </div>
  );
}
